import { ModuleTree } from "vuex";
import b64toBlob from "b64-to-blob";
import { saveAs } from "file-saver";

import { GetMyInvoicesFalconRequest } from "@/interfaces/falcon/get-my-invoices/get-my-invoices-falcon-request";
import { GetMyInvoicesFalconResponse } from "@/interfaces/falcon/get-my-invoices/get-my-invoices-falcon-response";
import { GetMyInvoicesInvoiceListItem } from "@/interfaces/falcon/get-my-invoices/get-my-invoices-invoice-list-item";
import { DoDocumentPrintFalconRequest } from "@/interfaces/falcon/do-document-print/do-document-print-falcon-request";
import { DoDocumentPrintFalconResponse } from "@/interfaces/falcon/do-document-print/do-document-print-falcon-response";

import { FalconService } from "@/services/falcon.service";

import { RequestAlias } from "@/enums/request-alias.enum";

/**
 * @author Damian Kajzer <damian.kajzer@sowlgroup.com>
 * Falcon documents vuex module
 */
export const FalconDocumentsModule: ModuleTree<unknown> = {
  falconDocuments: {
    namespaced: true,
    state: {
      documentsData: [],
      documentsTotalCount: 0,
      documentDetailsData: {}
    },
    actions: {
      /**
       * Fetches user documents
       * @param commit Vuex commit
       * @param request Request data
       */
      getDocuments({ commit }, request: GetMyInvoicesFalconRequest) {
        FalconService.request<
          GetMyInvoicesFalconRequest,
          GetMyInvoicesFalconResponse
        >(RequestAlias.GetMyInvoices, request).then(response => {
          if (
            response.data.getMyInvoicesResponse.invoiceList?.invoice?.length
          ) {
            const {
              invoiceList,
              invoicesCount
            } = response.data.getMyInvoicesResponse;
            commit("documentsReceived", invoiceList.invoice);
            commit("documentsTotalCountReceived", +invoicesCount);
          } else {
            commit("documentsReceived", []);
            commit("documentsTotalCountReceived", 0);
          }
        });
      },

      /**
       * Fetches user document details
       * @param commit Vuex commit
       * @param request Request data
       */
      getDocumentDetails({ commit }, request: GetMyInvoicesFalconRequest) {
        FalconService.request<
          GetMyInvoicesFalconRequest,
          GetMyInvoicesFalconResponse
        >(RequestAlias.GetMyInvoicesDetails, request).then(response => {
          if (
            response.data.getMyInvoicesResponse.invoiceList?.invoice?.length
          ) {
            commit(
              "documentDetailsReceived",
              response.data.getMyInvoicesResponse.invoiceList?.invoice[0]
            );
          }
        });
      },

      /**
       * Downloads document file in PDF format
       * @param commit Vuex commit
       * @param request Request data
       */
      getDocumentPrint({ commit }, request: DoDocumentPrintFalconRequest) {
        FalconService.request<
          DoDocumentPrintFalconRequest,
          DoDocumentPrintFalconResponse
        >(RequestAlias.GetInvoicePrint, request).then(response => {
          const { pdfData } = response.data.doDocumentPrintResponse;
          if (pdfData) {
            const contentType = "application/pdf";
            const blob: Blob = new Blob([b64toBlob(pdfData, contentType)], {
              type: contentType
            });
            saveAs(blob, `${request.doDocumentPrint.documentId}.pdf`);
          }
        });
      }
    },
    mutations: {
      /**
       * Updates user documents data
       * @param state Vuex state
       * @param documentsData Received data
       */
      documentsReceived(state, documentsData: GetMyInvoicesInvoiceListItem[]) {
        state.documentsData = documentsData;
      },

      /**
       * Updates documents total count
       * @param state Vuex state
       * @param documentsCount Documents total count
       */
      documentsTotalCountReceived(state, documentsCount: number) {
        state.documentsTotalCount = documentsCount;
      },

      /**
       * Updates document details data
       * @param state Vuex state
       * @param documentDetailsData Received document details data
       */
      documentDetailsReceived(
        state,
        documentDetailsData: GetMyInvoicesInvoiceListItem
      ) {
        state.documentDetailsData = documentDetailsData;
      }
    }
  }
};
